import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

const operationalStatuses = [
  ["N", "Normal working at the crossing"],
  ["A", "An attendant is required throughout"],
  ["E", "Road signals, sirens or bells are switched off"],
  ["Q", "An attendant is required some of the time"],
  ["W", "Wrong-direction movements must be cautioned or stopped"],
  ["C", "All movements must be cautioned"],
];

const valueOrDash = (value) => value === undefined || value === null || value === "" ? "Not specified" : value;

const RecordGrid = ({ items }) => (
  <dl className="wsm-record-grid">
    {items.map(({ label, value }) => <div key={label}><dt>{label}</dt><dd>{valueOrDash(value)}</dd></div>)}
  </dl>
);

const Rt3199 = (props) => {
  const { handleInputChange } = props;
  const locationData = props.locationData || {};
  const possessionData = props.possessionData || {};
  const worksiteData = props.worksiteData || {};
  const lineData = props.lineData || {};
  const rt3199Data = useMemo(() => props.rt3199Data || {}, [props.rt3199Data]);
  const conversionUnit = props.conversionUnit || "milesYards";
  const possessionIndex = props.possessionIndex;
  const worksiteIndex = props.worksiteIndex;
  const lineIndex = props.lineIndex;
  const valuePath = `possessions.${possessionIndex}.worksites.${worksiteIndex}.lines_at_site.${lineIndex}.rt3199`;
  const primaryEs = props.esUserData?.[0] || {};
  const { user: authUser } = useSelector((state) => state.auth);
  const employer = authUser?.organisation?.name || rt3199Data.employer || "";
  const mileageLabel = conversionUnit === "milesYards" ? "Miles / yards" : conversionUnit === "milesChains" ? "Miles / chains" : "Km / metres";

  useEffect(() => {
    const esId = primaryEs.user_id || "";
    if (rt3199Data.employer === employer && rt3199Data.es_swl_id === esId) return;
    handleInputChange(valuePath, { ...rt3199Data, employer, es_swl_id: esId }, { systemSync: true });
  }, [employer, handleInputChange, primaryEs.user_id, rt3199Data, valuePath]);

  const levelCrossings = useMemo(() => (possessionData.level_crossings || []).filter((crossing) => (
    crossing?.level_crossing && (crossing.lines || []).some((line) => line.line_reference === lineData.line_reference)
  )), [lineData.line_reference, possessionData.level_crossings]);

  return (
    <div className="wsm-rt3199">
      <section className="wsm-record-section">
        <div className="wsm-record-section__heading"><h4>Record details</h4><p>Details inherited from the pack, assigned supervisor and line.</p></div>
        <RecordGrid items={[
          { label: "Line affected", value: lineData.name },
          { label: "Engineering Supervisor", value: primaryEs.name },
          { label: "Employer", value: employer },
          { label: "WON item number", value: locationData.won_item_no },
        ]} />
      </section>

      <div className="wsm-rt3199__limits">
        <section className="wsm-record-section">
          <div className="wsm-record-section__heading"><h4>Possession limits</h4><p>{mileageLabel}</p></div>
          <RecordGrid items={[{ label: "Start", value: lineData.poss_start?.[conversionUnit] }, { label: "End", value: lineData.poss_end?.[conversionUnit] }]} />
        </section>
        <section className="wsm-record-section">
          <div className="wsm-record-section__heading"><h4>Worksite limits</h4><p>{mileageLabel}</p></div>
          <RecordGrid items={[{ label: "Start", value: lineData.start?.[conversionUnit] }, { label: "End", value: lineData.end?.[conversionUnit] }]} />
        </section>
      </div>

      <section className="wsm-record-section">
        <div className="wsm-record-section__heading"><h4>Level crossings</h4><p>Crossings associated with this line in {worksiteData.worksite_ref || "the worksite"}.</p></div>
        {levelCrossings.length === 0 ? <p className="wsm-record-section__empty">No level crossings are associated with this line.</p> : (
          <div className="wsm-rt3199__crossings">
            {levelCrossings.map((crossing, index) => <RecordGrid items={[{ label: "Level crossing", value: crossing.level_crossing }, { label: "Operational status", value: crossing.operational_status }]} key={`${crossing.level_crossing}-${index}`} />)}
          </div>
        )}
        <div className="wsm-status-guide wsm-status-guide--record">
          <dl>{operationalStatuses.map(([code, description]) => <div key={code}><dt>{code}</dt><dd>{description}</dd></div>)}</dl>
        </div>
      </section>
    </div>
  );
};

export default Rt3199;
